import { i18n } from "@/plugins/i18n";

export interface ReportTableHeader {
  title: string;
  key: string;
  sortable: boolean;
}

// Trend is "negative" for the user (more warnings) when it grows, hence the inverted colors
export const getReportTrendClass = (trend: number): string => {
  if (trend > 0) return "text-error";
  if (trend < 0) return "text-success";
  return "";
};

export const formatReportTrend = (trend: number): string => {
  if (trend > 0) return `+${trend}% ↑`;
  if (trend < 0) return `${trend}% ↓`;
  return `${trend}%`;
};

/**
 * Truncates a number to two decimals, without rounding.
 * Note the falsy check: `undefined`, `null`, `NaN`, `-0` and `0` all return `0`, so this cannot
 * distinguish "no data" from a measured zero — callers that need that distinction must check
 * before calling.
 * @param value - The number to truncate.
 * @returns The truncated number, or `0` for any falsy input.
 */
export const truncateToTwoDecimals = (value?: number): number => {
  if (!value) return 0;
  // toPrecision before truncating: `77.99 * 100` is 7798.999999999999 in binary floating point,
  // so a bare Math.trunc dropped a cent from any value whose scaled form lands just under an
  // integer — 77.99 came out as 77.98. Normalising the scaled value first keeps truncation
  // truncating (1.999 stays 1.99) without inheriting the representation error.
  const scaled = Number((value * 100).toPrecision(12));
  return parseFloat((Math.trunc(scaled) / 100).toFixed(2));
};

/**
 * Formats a count for a report, with the active locale's thousands separators.
 * Use this for every number a report renders, so a narrative never prints a bare `7259`
 * beside a KPI tile's `9,554`.
 * Grouped for the ACCOUNT's display language, not the browser's: `src/App.vue` drives
 * `i18n.global.locale` from the account setting, so a bare `toLocaleString()` would print
 * English-grouped numbers inside an otherwise German report.
 * Non-finite input is rendered as zero rather than passed through — `NaN.toLocaleString()` is
 * `"NaN"` and `Infinity` is `"∞"`, and this report is exported to PDF for customers.
 * @param value - The count as it arrives in the response; `undefined` is treated as zero.
 * @returns The localised digit string.
 */
export const formatReportNumber = (value?: number): string =>
  (Number.isFinite(value) ? (value as number) : 0).toLocaleString(i18n.global.locale.value);

/**
 * Formats a percentage for a report: truncated to two decimals, always with a `.` separator.
 * Deliberately NOT locale-formatted. Percentages reach the page through charts, captions and
 * interpolated narratives, and only some of those paths localise — localising here produced
 * "77,99%" in one part of a de-DE report and "77.99%" in another.
 * Callers append the `%` themselves, because most of them interpolate the number into an i18n
 * string that already carries the sign — hence the name says "value", not "percent".
 * @param value - The percentage, where `50` means 50%; `undefined` is treated as zero.
 * @returns The number as a string, with no percent sign and a `.` decimal separator.
 */
export const formatReportPercentValue = (value?: number): string =>
  `${truncateToTwoDecimals(value)}`;

/**
 * Builds a report trend's "vs. previous period" caption.
 * Lives here rather than in the chart component because the KPI strip renders the caption in its
 * own grid row — the mock puts it on the label's baseline, not under the sparkline — so two
 * components need the same string.
 * @param delta - The percentage change; `undefined` when the backend sent no prior-period baseline.
 * @returns An HTML string carrying a direction arrow, or `""` when there is nothing to compare.
 */
export const formatReportTrendCaption = (delta?: number): string => {
  // `== null` on purpose, to catch the JSON `null` as well as `undefined`: the store assigns the
  // response untyped, so a `null` delta reaches here, and it is neither `=== undefined` nor
  // `=== 0` nor `> 0` — it used to fall through to the down arrow and assert a decline that was
  // never measured. `NaN` behaved identically.
  if (delta == null || Number.isNaN(delta)) {
    return "";
  }
  // The direction is read off the magnitude as DISPLAYED, not off the raw sign. A delta of 0.004
  // truncates to "0", and an arrow beside "0%" contradicts this function's own "no change"
  // branch, so anything that renders as zero takes that branch instead.
  const magnitude = formatReportPercentValue(Math.abs(delta));
  if (Number(magnitude) === 0) {
    return i18n.global.t("reports.executiveSummaryReport.noChangeVsPreviousPeriod");
  }
  const arrow = delta > 0 ? "&#8593;" : "&#8595;";
  const suffix = i18n.global.t("reports.executiveSummaryReport.vsPreviousPeriod");
  return `${arrow} ${magnitude}% ${suffix}`;
};

// Builds the shared "Warning-Only Email Events" details table headers.
// The first column differs between the workspace (user) and MSP (workspace) reports,
// and labels are resolved from the report's own translation namespace.
export const getWarningEmailEventsTableHeaders = (
  t: (key: string) => string,
  options: { translationPrefix: string; firstColumnKey: string; firstColumnLabel: string }
): ReportTableHeader[] => {
  const { translationPrefix, firstColumnKey, firstColumnLabel } = options;
  return [
    {
      title: t(`${translationPrefix}.table.${firstColumnLabel}`),
      key: firstColumnKey,
      sortable: false,
    },
    { title: t(`${translationPrefix}.table.total`), key: "total", sortable: true },
    { title: t(`${translationPrefix}.table.trend`), key: "trend", sortable: true },
    {
      title: t(`${translationPrefix}.table.newDomain`),
      key: "newlyRegisteredDomain",
      sortable: true,
    },
    {
      title: t(`${translationPrefix}.table.encrypted`),
      key: "encryptedAttachment",
      sortable: true,
    },
    { title: t(`${translationPrefix}.table.unverified`), key: "unverifiedSender", sortable: true },
    {
      title: t(`${translationPrefix}.table.unknown`),
      key: "unknownExternalSender",
      sortable: true,
    },
  ];
};
